import React, { useEffect, useState } from 'react';
import { Post, Post as PostModel } from 'redux/models/post';
import { Post as PostComponent } from 'components/Post';
import { PostEvent } from 'components/PostEvent';
import './PostsList.scss';
import { useDispatch } from 'react-redux';
import { getSubposts, likePost, likeSubpost } from 'redux/thunks/team';
import { SendTokensModal } from 'components/Modals/SendTokens';
import { UserIdName } from 'redux/models/userIdName';
import { Team } from 'redux/models/team';
import { DeletePostModal } from 'components/Modals/DeletePostModal';
import { Subpost } from 'redux/models/subpost';

interface PostsListProps {
	postsProp: Post[];
	userId: string;
	subpostsLoading: boolean;
	currentTeam: Team | undefined;
	condensedPosts?: boolean;
	deletingPost?: boolean;
	deletingSubpost?: boolean;
}

const PostsList: React.FC<PostsListProps> = (props: PostsListProps) => {
	const { postsProp, userId, subpostsLoading, currentTeam, condensedPosts, deletingPost, deletingSubpost } = props;
	const dispatch = useDispatch();
	const [sendTokensModalOpen, setSendTokensModalOpen] = useState(false);
	const [postIdForSubposts, setPostIdForSubposts] = useState('');
	const [selectedPostRecipients, setSelectedPostRecipients] = useState<UserIdName[] | undefined>([]);
	const [posts, setPosts] = useState<Post[]>([]);
	const [postToDelete, setPostToDelete] = useState<Post | Subpost>();
	const [deleteSubpostType, setDeleteSubpostType] = useState<boolean>(false);

	const addSubpostClickedOnPost = (postId: string) => {
		setPostIdForSubposts(postId);
		setSendTokensModalOpen(true);
		const recipients = posts.find((post: PostModel) => post.postId === postId)?.users;
		setSelectedPostRecipients(recipients);
	};

	const likePostClicked = (teamId: string, postId: string) => {
		dispatch(likePost(teamId, postId));
	};

	const likeSubpostClicked = (postId: string, subpostId: string) => {
		dispatch(likeSubpost(postId, subpostId));
	};

	const showSubpostsClickedOnPost = (postId: string, showSubposts: boolean) => {
		setPosts(posts.map(post => (post.postId === postId ? { ...post, showSubposts: showSubposts } : post)));
		dispatch(getSubposts(postId));
	};

	const deletePostClicked = (post: Post) => {
		setDeleteSubpostType(false);
		setPostToDelete(post);
	};

	const deleteSubpostClicked = (subpost: Subpost) => {
		setDeleteSubpostType(true);
		setPostToDelete(subpost);
	};

	const hasCurrentUserLikedPost = function (post: PostModel) {
		return post.likes?.some(like => like.id === userId);
	};

	useEffect(() => {
		if (postsProp.length > 0) {
			setPosts((prevPosts: Post[]) => {
				if (prevPosts.length === 0 || prevPosts.length < postsProp.length) {
					return postsProp;
				} else {
					let updatedPosts = postsProp.map((post: Post, index: number) => {
						return { ...post, showSubposts: prevPosts[index].showSubposts };
					});
					return updatedPosts;
				}
			});
		}
	}, [postsProp]);

	return (
		<>
			{posts.map((post: Post, index: number) => {
				if (post.refType === 'LeaveEvent' || post.refType === 'TokenCountClearedEvent') {
					return <PostEvent key={index} post={post} />;
				} else {
					return (
						<PostComponent
							key={index}
							post={post}
							userId={userId}
							currentUserLikedPost={hasCurrentUserLikedPost(post)}
							subpostsLoading={subpostsLoading}
							currentTeam={currentTeam}
							addSubpostClicked={addSubpostClickedOnPost}
							likePostClicked={likePostClicked}
							likeSubpostClicked={likeSubpostClicked}
							showSubpostClicked={showSubpostsClickedOnPost}
							condensed={condensedPosts}
							deletingPost={deletingPost}
							deletingSubpost={deletingSubpost}
							deletePostClicked={deletePostClicked}
							handleDeleteSubpostClicked={deleteSubpostClicked}
							showAdminItems={currentTeam?.isAdmin}
						/>
					);
				}
			})}
			<SendTokensModal
				open={sendTokensModalOpen}
				postId={postIdForSubposts}
				postRecipients={selectedPostRecipients}
				onCloseClicked={() => setSendTokensModalOpen(false)}
			/>
			<DeletePostModal
				open={postToDelete !== undefined}
				post={postToDelete}
				isSubpost={deleteSubpostType}
				onCloseClicked={() => setPostToDelete(undefined)}
			/>
		</>
	);
};

PostsList.defaultProps = {
	condensedPosts: false,
	deletingPost: false,
	deletingSubpost: false,
};

export default PostsList;
